import type {
  MileageClaimRead,
  MicropaymentRead,
  ParticipantVisitRead,
  PaymentAccountRead,
  ReceiptClaimRead,
  StipendDetailsRead,
} from "@/api-v3";
import { claimRaisedByStudyTeam } from "@/features/payments/helpers/raised-by-study-team";
import type { PaymentAccount } from "@/features/payments/types/payment-account";
import { parseAmount } from "@/lib/amount";

import type {
  PaymentAccountSummary,
  PaymentDetailsView,
  VisitSummary,
} from "../types";

const last4 = (identifier?: string | null): string | undefined => {
  if (!identifier) return undefined;
  const trimmed = identifier.trim();
  if (!trimmed) return undefined;
  return trimmed.length > 4 ? trimmed.slice(-4) : trimmed;
};

const visitFromParticipantVisit = (
  participantVisit?: ParticipantVisitRead | null
): VisitSummary | undefined => {
  if (!participantVisit) return undefined;
  const name = participantVisit.visit?.name;
  if (!name) return undefined;
  return {
    name,
    date: participantVisit.visit_date,
    referenceId: optional(participantVisit.visit?.reference_id),
    visitType: optional(participantVisit.visit_type?.name),
  };
};

const accountFromClaimEmbedded = (
  account?: PaymentAccountRead | null
): PaymentAccountSummary | undefined => {
  if (!account) return undefined;
  return {
    type: account.type,
    label: account.title,
    identifier: last4(account.identifier),
  };
};

const accountFromActive = (
  account?: PaymentAccount | null
): PaymentAccountSummary | undefined => {
  if (!account) return undefined;
  return {
    type: account.target_account_type,
    label: account.target_account_title,
    identifier: last4(account.target_account_identifier),
  };
};

const optional = (value: string | null | undefined): string | undefined =>
  value ?? undefined;

const adjustedAmount = (
  amount: string,
  approvedAmount: string | null | undefined
): string | undefined => {
  if (approvedAmount == null) return undefined;
  const approved = parseAmount(approvedAmount);
  if (approved == null || approved === parseAmount(amount)) return undefined;
  return approvedAmount;
};

export const normalizeMileageClaim = (
  claim: MileageClaimRead
): PaymentDetailsView => ({
  paymentKind: "mileage",
  id: claim.id,
  amount: claim.amount,
  adjustedAmount: adjustedAmount(claim.amount, claim.approved_amount),
  currency: claim.currency,
  status: (claim.status ?? "").toLowerCase(),
  createdAt: claim.created_at,
  externalNotes: optional(claim.external_notes),
  payoutStatus: optional(claim.payout?.status),
  payoutFailureReason: optional(claim.payout?.failure_reason),
  decisionDate: optional(claim.details.reviewed_at),
  paymentAccount: accountFromClaimEmbedded(claim.payment_account),
  visit: visitFromParticipantVisit(claim.details.participant_visit),
  raisedByStudyTeam: claimRaisedByStudyTeam(claim),
  details: {
    kind: "mileage",
    distance: claim.details.distance,
    unit: claim.details.unit,
    ratePerUnit: claim.details.rate_per_unit,
    total: claim.details.total,
  },
});

export const normalizeReceiptClaim = (
  claim: ReceiptClaimRead
): PaymentDetailsView => ({
  paymentKind: "receipt",
  id: claim.id,
  amount: claim.amount,
  adjustedAmount: adjustedAmount(claim.amount, claim.approved_amount),
  currency: claim.currency,
  status: (claim.status ?? "").toLowerCase(),
  createdAt: claim.created_at,
  externalNotes: optional(claim.external_notes),
  payoutStatus: optional(claim.payout?.status),
  payoutFailureReason: optional(claim.payout?.failure_reason),
  decisionDate: optional(claim.details.reviewed_at),
  paymentAccount: accountFromClaimEmbedded(claim.payment_account),
  visit: visitFromParticipantVisit(claim.participant_visit),
  raisedByStudyTeam: claimRaisedByStudyTeam(claim),
  details: {
    kind: "receipt",
    imageUrl: claim.details.signed_image_url ?? claim.details.image_url,
    merchant: optional(claim.details.merchant_name),
    receiptDate: optional(claim.details.receipt_date),
    tax: optional(claim.details.tax),
    discount: optional(claim.details.discount),
    additionalFees: optional(claim.details.additional_fees),
    items: (claim.details.items ?? []).map((item) => ({
      description: item.description,
      amount: item.amount,
      category: item.category,
    })),
  },
});

export const normalizeMicropayment = (
  claim: MicropaymentRead
): PaymentDetailsView => ({
  paymentKind: "micropayment",
  id: claim.id,
  amount: claim.amount,
  currency: claim.currency,
  status: (claim.status ?? "").toLowerCase(),
  createdAt: claim.created_at,
  externalNotes: optional(claim.external_notes),
  payoutStatus: optional(claim.payout?.status),
  payoutFailureReason: optional(claim.payout?.failure_reason),
  decisionDate: optional(claim.actioned_at),
  paymentAccount: claim.payment_account
    ? accountFromActive(claim.payment_account as unknown as PaymentAccount)
    : undefined,
  raisedByStudyTeam: false,
  details: {
    kind: "micropayment",
    configurationLabel: claim.micropayment_configuration?.name,
  },
});

export const normalizeStipend = (
  claim: StipendDetailsRead,
  activeAccount?: PaymentAccount | null
): PaymentDetailsView => ({
  paymentKind: "stipend",
  id: claim.id,
  amount: claim.amount,
  currency: claim.currency,
  status: (claim.status ?? "").toLowerCase(),
  createdAt: claim.created_at,
  externalNotes: optional(claim.external_notes),
  payoutStatus: optional(claim.payout?.status),
  payoutFailureReason: optional(claim.payout?.failure_reason),
  decisionDate: optional(claim.actioned_at),
  paymentAccount: accountFromActive(activeAccount),
  visit: visitFromParticipantVisit(claim.participant_visit),
  raisedByStudyTeam: false,
  details: { kind: "stipend" },
});
